"use client";

import { Logout, Report, Settings, User } from "@carbon/react/icons";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useId, useRef, useState } from "react";
import { logoutAction } from "@/app/login/actions";
import { SideNavRailTooltip } from "@/components/SideNavRailTooltip";
import { usersCopy } from "@/lib/ui/admin";
import { isAdminNavActive } from "@/lib/ui/nav";
import { userInitials } from "@/lib/ui/user-initials";
import { profileCopy, profileHref } from "@/lib/ui/profile";
import {
  isReportsNavActive,
  reportsCopy,
  reportsHref,
} from "@/lib/ui/reports";

type UserMenuProps = {
  name: string;
  email?: string;
  expanded?: boolean;
  showAdmin?: boolean;
  adminHref?: string;
  showReports?: boolean;
};

export function UserMenu({
  name,
  email,
  expanded = true,
  showAdmin = false,
  adminHref,
  showReports = false,
}: UserMenuProps) {
  const pathname = usePathname();
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const initials = userInitials(name);
  const adminActive = showAdmin && isAdminNavActive(pathname);
  const reportsActive = showReports && isReportsNavActive(pathname);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="side-nav-user">
      <SideNavRailTooltip label={name} show={!expanded && !open}>
        <button
          type="button"
          className="side-nav-user-trigger"
          aria-label={name}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={menuId}
          onClick={() => setOpen((value) => !value)}
        >
          <span className="header-avatar" aria-hidden>
            {initials}
          </span>
          {expanded ? (
            <span className="side-nav-user-copy">
              <span className="side-nav-user-name">{name}</span>
              {email ? (
                <span className="side-nav-user-email">{email}</span>
              ) : null}
            </span>
          ) : null}
        </button>
      </SideNavRailTooltip>
      {open ? (
        <ul id={menuId} className="side-nav-user-menu" role="menu">
          <li role="none">
            <NextLink
              href={profileHref()}
              role="menuitem"
              className="side-nav-user-item"
              onClick={() => setOpen(false)}
            >
              <User size={20} />
              {profileCopy.nav}
            </NextLink>
          </li>
          {showReports ? (
            <li role="none">
              <NextLink
                href={reportsHref()}
                role="menuitem"
                className={[
                  "side-nav-user-item",
                  reportsActive ? "is-current" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-current={reportsActive ? "page" : undefined}
                onClick={() => setOpen(false)}
              >
                <Report size={20} />
                {reportsCopy.nav}
              </NextLink>
            </li>
          ) : null}
          {showAdmin && adminHref ? (
            <li role="none">
              <NextLink
                href={adminHref}
                role="menuitem"
                className={[
                  "side-nav-user-item",
                  adminActive ? "is-current" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-current={adminActive ? "page" : undefined}
                onClick={() => setOpen(false)}
              >
                <Settings size={20} />
                {usersCopy.nav}
              </NextLink>
            </li>
          ) : null}
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className="side-nav-user-item side-nav-user-item--logout"
              onClick={() => {
                setOpen(false);
                void logoutAction();
              }}
            >
              <Logout size={20} />
              {profileCopy.logout}
            </button>
          </li>
        </ul>
      ) : null}
    </div>
  );
}
